import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  DropdownButton,
  DropdownPanel,
  DropdownWrapper,
  StyledIconCaretSelect,
  StyledSearchSelect,
} from './component.styles'

const KEY_ARROW_DOWN = 'ArrowDown'
const KEY_ARROW_UP = 'ArrowUp'
const KEY_ENTER = 'Enter'
const KEY_ESCAPE = 'Escape'
const KEY_TAB = 'Tab'

const CustomPanelDropdown = ({
  className,
  dataTestId,
  defaultLabel,
  dropdownButtonIcon,
  onChange,
  options = [],
  renderPanel,
}) => {
  const wrapperRef = useRef(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1)
      return undefined
    }

    const closeOnOutsideClick = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const closeOnEscape = event => {
      if (event.key === KEY_ESCAPE) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('click', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [isOpen])

  const close = () => setIsOpen(false)

  const onSelect = option => {
    close()
    onChange?.(option)
  }

  const handleKeyDown = event => {
    if (
      event.type !== 'click' &&
      ![KEY_ESCAPE, KEY_ENTER, KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_TAB].includes(
        event.key,
      )
    ) {
      return
    }

    if ([KEY_ESCAPE, KEY_TAB].includes(event.key)) {
      setHighlightedIndex(-1)
      close()
      return
    }

    if (event.key === KEY_ENTER) {
      event.preventDefault()
      if (highlightedIndex >= 0 && options[highlightedIndex]) {
        onSelect(options[highlightedIndex])
      } else {
        setIsOpen(open => !open)
      }
      return
    }

    if (event.type === 'click') {
      setIsOpen(open => !open)
      return
    }

    if ([KEY_ARROW_DOWN, KEY_ARROW_UP].includes(event.key)) {
      event.preventDefault()

      if (!options.length) {
        return
      }

      if (!isOpen) {
        setIsOpen(true)
        setHighlightedIndex(
          event.key === KEY_ARROW_DOWN ? 0 : options.length - 1,
        )
        return
      }

      if (event.key === KEY_ARROW_DOWN) {
        setHighlightedIndex(index =>
          index < options.length - 1 ? index + 1 : 0,
        )
      } else {
        setHighlightedIndex(index =>
          index > 0 ? index - 1 : options.length - 1,
        )
      }
      return
    }

    setHighlightedIndex(-1)
  }

  const highlightedOptionId =
    highlightedIndex >= 0 ? options[highlightedIndex]?.id : undefined

  return (
    <DropdownWrapper
      className={className}
      data-testid={dataTestId}
      ref={wrapperRef}
    >
      <DropdownButton
        aria-expanded={isOpen}
        aria-haspopup="true"
        data-testid={`${dataTestId}-button`}
        onClick={handleKeyDown}
        onKeyDown={handleKeyDown}
        type="button"
      >
        {dropdownButtonIcon}
        <span>{defaultLabel}</span>
        <StyledIconCaretSelect $isOpen={isOpen} />
      </DropdownButton>
      {isOpen && (
        <DropdownPanel data-testid={`${dataTestId}-options`}>
          {renderPanel({
            close,
            dataTestId,
            highlightedOptionId,
            onSelect,
          })}
        </DropdownPanel>
      )}
    </DropdownWrapper>
  )
}

CustomPanelDropdown.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  defaultLabel: PropTypes.string,
  dropdownButtonIcon: PropTypes.node,
  onChange: PropTypes.func,
  options: PropTypes.array,
  renderPanel: PropTypes.func.isRequired,
}

const AdminSearchSelect = ({ renderPanel, className, ...props }) => {
  if (renderPanel) {
    return (
      <CustomPanelDropdown
        className={className}
        renderPanel={renderPanel}
        {...props}
      />
    )
  }

  return <StyledSearchSelect className={className} {...props} />
}

AdminSearchSelect.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  defaultLabel: PropTypes.string,
  dropdownButtonIcon: PropTypes.node,
  keyProp: PropTypes.string,
  labelProp: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
  renderPanel: PropTypes.func,
  shouldHideSelectedIcon: PropTypes.bool,
}

export default AdminSearchSelect
